import {useReducer} from 'react';
import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useSignIn, type SignInBody} from 'api/generated';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
// import {APPLE_SVG, GOOGLE_SVG} from 'lib';
import {usePersistentStore} from 'stores';
import {
  Transitioner,
  Button,
  InputSecret,
  InputText,
  //  ButtonOmniAuth,
  //  TextWithLine
} from 'components';

import ModalResetPassword from './Modal.ResetPassword';

const schema = z.object({
  email: z.string().email('Email is invalid'),
  password: z.string().min(6, 'Password must be at least 6 character(s)'),
});

interface IFormSignInProps {}

const FormSignIn = ({}: IFormSignInProps) => {
  const [showModal, toggleModal] = useReducer(s => !s, false);
  const setAuthToken = usePersistentStore(s => s.setAuthToken);

  const signIn = useSignIn();
  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: SignInBody) => {
    const {data: res} = await signIn.mutateAsync({data});
    if (res.data) {
      setAuthToken(res.data.jwt);
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  return (
    <section className="flex flex-col flex-1 p-6 lg:px-8">
      <Transitioner>
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h1 className="text-center text-2xl/9 font-bold tracking-tight dark:text-white">Sign in to your account</h1>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          {/* TODO: Implement this */}
          {/* <div className="flex flex-col gap-6 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <ButtonOmniAuth svg={GOOGLE_SVG} text="Google" />
              <ButtonOmniAuth svg={APPLE_SVG} text="Apple" />
            </div>
            <TextWithLine text="Or continue with" />
          </div> */}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
            <InputText label="Email Address" type="email" autoComplete="email" autoFocus {...register('email')} error={errors.email?.message} />
            <InputSecret
              label="Password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
              error={errors.password?.message}
            />
            <div className="mt-5">
              <Button type="submit" text="Sign in" fullWidth size="small" loading={signIn.isPending} />
            </div>
          </form>

          <p className="mt-10 text-center text-sm/6">
            No account?{' '}
            <Link to="/sign-up" className="font-semibold text-primary hover:brightness-115 transition duration-200">
              Sign up here
            </Link>
          </p>

          <div className="flex items-center justify-center mt-1.5">
            <div className="text-sm/6">
              <button
                type="button"
                onClick={toggleModal}
                className="font-semibold text-primary hover:brightness-115 transition duration-200 cursor-pointer">
                Forgot password?
              </button>
            </div>
          </div>
        </div>
      </Transitioner>

      <ModalResetPassword visible={showModal} onClose={toggleModal} />
    </section>
  );
};

export default FormSignIn;
